import { IafFetch, IafPassSvc, IafItemSvc, IafUserConfig, IafProj, IafDataSource, IafNotification, IafScripts, IafFile, IafUserGroup, IafSession } from '@dtplatform/platform-api';
import { getConfig } from '../config';
import type {
  PlatformContext,
  Application,
  Project,
  UserGroup,
  User,
  UserConfig
} from '../types/config';

// Initialize the platform API with configuration
export function initializePlatformApi(): void {
  const config = getConfig();

  // Set up IafFetch configuration
  IafFetch.CONFIG.itemServiceOrigin = config.itemServiceOrigin;
  IafFetch.CONFIG.passportServiceOrigin = config.passportServiceOrigin;
  IafFetch.CONFIG.fileServiceOrigin = config.fileServiceOrigin;
  IafFetch.CONFIG.datasourceServiceOrigin = config.datasourceServiceOrigin;
  IafFetch.CONFIG.graphicsServiceOrigin = config.graphicsServiceOrigin;
  IafFetch.CONFIG.notificationServiceOrigin = config.notificationServiceOrigin;
  IafFetch.CONFIG.workflowServiceOrigin = config.workflowServiceOrigin;

  // Set up IafSession configuration (needed for ScriptHelper)
  IafSession.setConfig(config);
}

/**
 * Set the current auth token in IafSession
 * This is needed for ScriptHelper and other ipa-core utilities
 */
export function setSessionAuthToken(authToken: string): void {
  IafSession.setAuthToken(authToken);
}


// Create a context object for API calls
export function createContext(authToken: string, namespaces: string[] = []): PlatformContext {
  return {
    authToken,
    _namespaces: namespaces,
  };
}

// Get current user
export async function getCurrentUser(ctx: PlatformContext): Promise<User> {
  return await IafPassSvc.getCurrentUser(ctx);
}

// Get applications for an organization
export async function getApplicationsForOrganization(
  organizationId: string,
  ctx: PlatformContext
): Promise<Application[]> {
  try {
    const result = await IafPassSvc.getApplications(
      { _orgId: organizationId },
      ctx,
      { _pageSize: 100 }
    );
    return result?._list || [];
  } catch (error) {
    console.error('Error fetching applications:', error);
    return [];
  }
}

// Get workspaces (projects) for the current user
export async function getProjects(ctx: PlatformContext): Promise<Project[]> {
  try {
    console.log('Fetching projects with ctx:', {
      hasToken: !!ctx.authToken,
      tokenPreview: ctx.authToken?.substring(0, 20) + '...',
      namespaces: ctx._namespaces
    });

    const result = await IafPassSvc.getWorkspaces(
      { _userType: 'project_workspace' },
      ctx,
      { _pageSize: 100 }
    );

    console.log('Projects API result:', result);
    return (result?._list || []) as Project[];
  } catch (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
}

// Get a specific workspace/project
export async function getProject(projectId: string, ctx: PlatformContext): Promise<Project | null> {
  try {
    return await IafPassSvc.getWorkspace(projectId, ctx) as Project;
  } catch (error) {
    console.error('Error fetching project:', error);
    return null;
  }
}

// Get user groups for a project
export async function getUserGroupsForProject(
  projectNamespaces: string[],
  ctx: PlatformContext
): Promise<UserGroup[]> {
  try {
    const projectCtx = { ...ctx, _namespaces: projectNamespaces };
    const result = await IafPassSvc.getUserGroups({}, projectCtx, { _pageSize: 100, includeAll: true });
    return result?._list || [];
  } catch (error) {
    console.error('Error fetching user groups:', error);
    return [];
  }
}

// Get a specific user group
export async function getUserGroup(userGroupId: string, ctx: PlatformContext): Promise<UserGroup | null> {
  try {
    return await IafPassSvc.getUserGroup(userGroupId, ctx);
  } catch (error) {
    console.error('Error fetching user group:', error);
    return null;
  }
}

// Get user config for a user group
export async function getUserConfig(
  userGroup: UserGroup,
  project: Project,
  ctx: PlatformContext,
  configName?: string
): Promise<UserConfig | null> {
  try {
    // Check if user group has userConfigs in _userAttributes
    const userConfigs = userGroup._userAttributes?.userConfigs;

    console.log('User group userConfigs:', userConfigs);

    if (!userConfigs || userConfigs.length === 0) {
      console.log('No userConfigs found in user group _userAttributes');
      return null;
    }

    // Find the right config - by name if provided, otherwise use the first one
    const configRef = configName
      ? userConfigs.find(uc => uc._name === configName)
      : userConfigs[0];

    if (!configRef) {
      console.log('Config not found with name:', configName);
      return null;
    }

    console.log('Found config reference:', configRef._name, configRef._id);

    // Use IafProj.getUserConfigs to get the full config with versions
    const configs = await IafProj.getUserConfigs(
      { _id: project._id, _namespaces: project._namespaces },
      { _id: configRef._id },
      ctx
    );

    console.log('getUserConfigs result:', configs);

    if (!configs || configs.length === 0) {
      console.log('No config data returned from IafProj.getUserConfigs');
      return null;
    }

    const configItem = configs[0];

    // Find the tip version
    if (configItem._versions && configItem._versions.length > 0) {
      const tipVersion = configItem._versions.find(v => v._version === configItem._tipVersion)
        || configItem._versions.find(v => v._isTip)
        || configItem._versions[0];

      console.log('Found tip version:', tipVersion._version);

      if (tipVersion._userData) {
        // _userData might be a string that needs parsing
        const userData = typeof tipVersion._userData === 'string'
          ? JSON.parse(tipVersion._userData)
          : tipVersion._userData;

        console.log('User config loaded successfully:', userData.settings?.appTitle);
        return userData as UserConfig;
      }
    }

    console.log('No _userData found in config versions');
    return null;
  } catch (error) {
    console.error('Error fetching user config:', error);
    return null;
  }
}

// Logout user
export async function logout(ctx: PlatformContext): Promise<void> {
  try {
    await IafPassSvc.logout(ctx);
  } catch (error) {
    console.error('Error during logout:', error);
  }
}

export { IafFetch, IafPassSvc, IafItemSvc, IafUserConfig, IafProj, IafDataSource, IafNotification, IafScripts, IafFile, IafUserGroup, IafSession };
